import {
  EliminatedPerson,
  extractAxiosErrorMessage,
  PeopleCandidate,
  PeopleRole,
  PromptAddendumModel,
  usePeopleDiscovery,
  usePeopleDiscoveryTrace,
  useOperation,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";

// The result blob the people test-run worker writes to the operation (dryRun path).
interface TestRunResult {
  status?: string;
  candidates?: PeopleCandidate[];
  // Everyone the ranking placed BELOW the shortlist, and everyone it excluded. A test
  // run persists nothing, so this result is the only place they can be seen - and the
  // judgement being made here ("is the 4th better than the 3rd?", "why was she
  // dropped?") is exactly the one the shortlist hides. Showing only the top three while
  // tuning the rules that pick the top three would be tuning blind.
  overflow?: EliminatedPerson[];
  eliminated?: EliminatedPerson[];
  companyName?: string;
  apolloTotalEntries?: number;
  apolloCallCount?: number;
  sampled?: boolean;
  traceSubjectRef?: string;
  traceRunId?: string;
}

// Which band each ranked person falls into, mirrored from PeopleDiscoveryPanel so the
// test preview reads the same as the live find-people results.
const BAND_HEADINGS: { band: string; label: string }[] = [
  { band: "primary", label: "Primary targets" },
  { band: "secondary", label: "Also strong" },
  { band: "sustainability", label: "Sustainability / ESG" },
  { band: "other", label: "Other" },
];

const roleLabelBadge = (roleLabel: string): React.ReactNode => {
  if (roleLabel === "reply_target")
    return (
      <Badge bg="primary" className="ms-1">
        Reply target
      </Badge>
    );
  if (roleLabel === "signer")
    return (
      <Badge bg="dark" className="ms-1">
        Signer
      </Badge>
    );
  return null;
};

// The contacts-ranking prompt's test panel (per role): runs the current draft against
// a real subject's chosen company as a long-running discovery op and renders the
// ranked bands + AI trace. Handed the live draft addendum by the shared editor; keyed
// on `role` by the card so a role switch remounts and clears it.
const PeopleRankingTestPanel: React.FC<{
  role: PeopleRole;
  addendum: PromptAddendumModel;
}> = ({ role, addendum }) => {
  const { testPeopleRankingPrompt } = usePeopleDiscovery();

  const [subjectKind, setSubjectKind] = useState<"voa" | "lead">("lead");
  const [subjectRef, setSubjectRef] = useState<string>("");
  const [testOp, setTestOp] = useState<{ key: string; siteId: string } | null>(
    null
  );
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestRunResult | null>(null);
  const [showTrace, setShowTrace] = useState<boolean>(false);

  const testOpQuery = useOperation(testOp?.key ?? null, testOp?.siteId ?? null);
  const testStatus = testOpQuery.data?.operation?.status;
  const testRunning =
    testOp !== null &&
    testStatus !== "completed" &&
    testStatus !== "failed" &&
    testStatus !== "cancelled";
  const progressMessage = testOpQuery.data?.operation?.progress?.message;

  useEffect(() => {
    if (!testOp || !testStatus) return;
    if (testStatus === "completed") {
      setTestResult((testOpQuery.data?.operation?.result as TestRunResult) ?? {});
      setTestOp(null);
    } else if (testStatus === "failed" || testStatus === "cancelled") {
      setTestError(
        testOpQuery.data?.operation?.error ?? "The test run did not complete."
      );
      setTestOp(null);
    }
  }, [testStatus, testOp, testOpQuery.data]);

  const handleTest = async () => {
    setTestError(null);
    setTestResult(null);
    setShowTrace(false);
    try {
      const res = await testPeopleRankingPrompt.mutateAsync({
        role,
        addendum,
        subjectRef: subjectRef.trim(),
        subjectKind,
      });
      setTestOp({ key: res.operationKey, siteId: res.siteId });
    } catch (e) {
      setTestError(
        (await extractAxiosErrorMessage(e)) ?? "Failed to start the test run."
      );
    }
  };

  const traceQuery = usePeopleDiscoveryTrace(
    testResult?.traceSubjectRef ?? null,
    testResult?.traceRunId ?? null,
    showTrace
  );

  const testCandidates = testResult?.candidates ?? [];
  // The worker sends the runners-up and the exclusions separately on a test run, so the
  // cut line itself is visible: the point of tuning is to move people across it.
  const testOverflow = testResult?.overflow ?? [];
  const testExcluded = testResult?.eliminated ?? [];

  return (
    <div>
      <strong>Test this prompt</strong>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
        Runs the current draft (saved rules plus any unsaved preamble edit) against a
        real {role} for its chosen company. Nothing is saved and the row is not
        changed. Apollo discovery is free; the ranking pass costs a few cents.
      </div>
      <div
        style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}
      >
        <div>
          <Form.Label style={{ fontSize: 12, marginBottom: 2 }}>Subject</Form.Label>
          <Form.Select
            size="sm"
            value={subjectKind}
            onChange={(e) => setSubjectKind(e.target.value as "voa" | "lead")}
            style={{ width: 180 }}
          >
            <option value="lead">Promoted lead id</option>
            <option value="voa">VOA assessment ref</option>
          </Form.Select>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Form.Control
            size="sm"
            value={subjectRef}
            placeholder={subjectKind === "voa" ? "e.g. 30799525000" : "lead id (UUID)"}
            onChange={(e) => setSubjectRef(e.target.value)}
          />
        </div>
        <Button
          size="sm"
          variant="outline-primary"
          disabled={
            testRunning || !subjectRef.trim() || testPeopleRankingPrompt.isLoading
          }
          onClick={() => void handleTest()}
        >
          {testRunning ? (
            <>
              <Spinner as="span" size="sm" animation="border" />{" "}
              {progressMessage ?? "Finding people..."}
            </>
          ) : (
            "Test run (free discovery)"
          )}
        </Button>
      </div>

      {testError && (
        <Alert variant="danger" style={{ marginTop: 8 }}>
          {testError}
        </Alert>
      )}

      {testResult && (
        <div style={{ marginTop: 10 }}>
          {testResult.status === "not_configured" ? (
            <Alert variant="warning">
              Apollo People Search is not configured in this environment, so the test
              could not run.
            </Alert>
          ) : testResult.status === "no_company" ? (
            <Alert variant="warning">
              No {role} company chosen for this row - pick one before testing.
            </Alert>
          ) : testResult.status === "quota_exhausted" ? (
            <Alert variant="warning">
              Apollo&apos;s daily discovery quota is used up - try again tomorrow.
            </Alert>
          ) : testResult.status &&
            !["complete", "no_people"].includes(testResult.status) ? (
            <Alert variant="danger">
              The test run did not complete (status: {testResult.status}). The
              subject row may have changed - try again.
            </Alert>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                  flexWrap: "wrap",
                }}
              >
                <strong>
                  {testCandidates.length} candidate
                  {testCandidates.length === 1 ? "" : "s"}
                </strong>
                {testResult.companyName && (
                  <span style={{ fontSize: 12, color: "#888" }}>
                    from {testResult.apolloTotalEntries ?? 0} at{" "}
                    {testResult.companyName}
                    {testResult.sampled && " (sampled)"}
                  </span>
                )}
                {testResult.traceRunId && (
                  <Button
                    size="sm"
                    variant="link"
                    style={{ fontSize: 12, padding: 0 }}
                    onClick={() => setShowTrace(true)}
                  >
                    View AI trace
                  </Button>
                )}
              </div>
              {testCandidates.length === 0 ? (
                <div style={{ fontSize: 12, color: "#666" }}>
                  Apollo found the company but no matching people in the UK. Open the
                  AI trace to see what it checked.
                </div>
              ) : (
                BAND_HEADINGS.map(({ band, label }) => {
                  const inBand = testCandidates.filter((c) => c.band === band);
                  if (inBand.length === 0) return null;
                  return (
                    <div key={band} className="mt-2">
                      <div
                        className="text-uppercase text-muted"
                        style={{ fontSize: 11, letterSpacing: 0.5 }}
                      >
                        {label}
                      </div>
                      {inBand.map((person) => (
                        <div
                          key={person.apolloId}
                          style={{ fontSize: 12, padding: "2px 0" }}
                        >
                          <strong>
                            {`${person.firstName} ${person.lastNameMasked}`.trim() ||
                              "(no name)"}
                          </strong>{" "}
                          - {person.title}
                          {roleLabelBadge(person.roleLabel)}
                          {person.reasoning && (
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              {person.reasoning}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })
              )}
              {testOverflow.length > 0 && (
                <div className="mt-3">
                  <div
                    className="text-uppercase text-muted"
                    style={{ fontSize: 11, letterSpacing: 0.5 }}
                  >
                    Below the shortlist - the operator sees these one click down
                  </div>
                  {testOverflow.map((person, i) => (
                    <div
                      key={person.apolloId ?? `${person.firstName}-${i}`}
                      style={{ fontSize: 12, padding: "2px 0" }}
                    >
                      <span className="text-muted">{i + 1}.</span>{" "}
                      <strong>
                        {`${person.firstName} ${person.lastNameMasked}`.trim() ||
                          "(no name)"}
                      </strong>{" "}
                      - {person.title}
                      {person.reasoning && (
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {person.reasoning}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {testExcluded.length > 0 && (
                <div className="mt-3">
                  <div
                    className="text-uppercase text-muted"
                    style={{ fontSize: 11, letterSpacing: 0.5 }}
                  >
                    Excluded by the ranking ({testExcluded.length})
                  </div>
                  {testExcluded.map((person, i) => (
                    <div
                      key={person.apolloId ?? `${person.firstName}-${i}`}
                      style={{ fontSize: 12, padding: "2px 0", color: "#666" }}
                    >
                      {`${person.firstName} ${person.lastNameMasked}`.trim() ||
                        "(no name)"}{" "}
                      - {person.title}
                      {person.reasoning && ` (${person.reasoning})`}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Modal show={showTrace} onHide={() => setShowTrace(false)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            Test run - AI trace ({role})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {traceQuery.isLoading ? (
            <div style={{ fontSize: 12, color: "#888" }}>Loading trace...</div>
          ) : traceQuery.data ? (
            <AITraceViewer trace={traceQuery.data} />
          ) : (
            <div style={{ fontSize: 12, color: "#888" }}>
              No AI trace available for this run.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PeopleRankingTestPanel;
